import { Skeleton } from '@mui/material';
import { t } from 'i18next';
import { CallbackSuccessHandler, FetchResult, useFetchCallback } from 'json-api-hooks';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import QRCode from 'react-qr-code';

import useSnackbar from '../../../hooks/useSnackbar';
import Form from '../../Form/Form';
import { FormFieldDefinition } from '../../Form/ResourceForm';
import { InputType } from '../../Form/types';
import Heading from '../../Heading';

enum FormType {
  SignIn,
  EnterOTP,
  ConnectOTP,
  Loading,
}

const DESCRIPTIONS: Record<FormType, string> = {
  [FormType.SignIn]: t('auth.signIn.description'),
  [FormType.EnterOTP]: t('auth.enterOTP.description'),
  [FormType.ConnectOTP]: t('auth.connectOTP.description'),
  [FormType.Loading]: '',
};
const FIELDS: Record<FormType, FormFieldDefinition[]> = {
  [FormType.SignIn]: [
    { attribute: 'email', placeholder: 'email@voorbeeld.nl', label: 'Email', type: InputType.Email },
    { attribute: 'password', placeholder: '*********', label: 'Wachtwoord', type: InputType.Password },
  ],
  [FormType.EnterOTP]: [
    { attribute: 'email', type: InputType.Hidden },
    { attribute: 'password', type: InputType.Hidden },
    { attribute: 'otp_attempt', label: 'Tweestapsverificatie', type: InputType.OTP },
  ],
  [FormType.ConnectOTP]: [
    { attribute: 'email', type: InputType.Hidden },
    { attribute: 'password', type: InputType.Hidden },
    {
      attribute: 'otp_code',
      label: 'Tweestapsverificatie',
      type: InputType.OTP,
      helper: 'Scan de QR code met een authenticatie app op je smartphone en voer de code in.',
    },
  ],
  [FormType.Loading]: [],
};

const ERROR_FIELD: Record<FormType, string> = {
  [FormType.SignIn]: 'password',
  [FormType.EnterOTP]: 'otp_attempt',
  [FormType.ConnectOTP]: 'otp_code',
  [FormType.Loading]: '',
};

const SignInForm = ({ redirectUrl }: { redirectUrl?: string }) => {
  const methods = useForm();
  const showSnackbar = useSnackbar();
  const [callback, loading] = useFetchCallback('/u/session/new', 'POST');
  const [formType, setFormType] = useState(FormType.SignIn);
  const [connectURI, setConnectURI] = useState<null | string>(null);

  const handleError = ({ json }: FetchResult) => {
    const error = (json as Record<string, string>).error;
    if (error === 'OTP_MISSING') {
      setFormType(FormType.ConnectOTP);
      setConnectURI((json as Record<string, string>).otp_uri);
    } else if (error === 'OTP_REQUIRED') {
      setFormType(FormType.EnterOTP);
    } else {
      const message = formType === FormType.EnterOTP ? 'Code komt niet overeen.' : (error ?? 'Er ging iets mis');

      methods.setError(ERROR_FIELD[formType], {
        message,
        type: 'server',
      });
    }
  };

  const handleSuccess: CallbackSuccessHandler = () => {
    showSnackbar('Welkom terug!');
    setFormType(FormType.Loading);

    if (redirectUrl) {
      window.location.href = redirectUrl;
    } else {
      window.location.reload();
    }
  };

  const onSubmit = methods.handleSubmit((data) => {
    callback({ user: data }).then(handleSuccess).catch(handleError);
  });

  if (formType === FormType.Loading) {
    return (
      <>
        <Heading>Inloggen</Heading>
        <Skeleton />
        <Skeleton width="60%" />
        <Skeleton width="80%" />
      </>
    );
  }

  return (
    <>
      <Heading>Inloggen</Heading>
      <Form
        description={
          <>
            {DESCRIPTIONS[formType]}
            {connectURI && <QRCode style={{ alignSelf: 'center', maxWidth: '100%' }} value={connectURI} />}
          </>
        }
        fields={FIELDS[formType]}
        loading={loading}
        methods={methods}
        submitLabel="Inloggen"
        onSubmit={onSubmit}
      />
    </>
  );
};

export default SignInForm;
